import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import { openDownloadAppModal } from 'src/components/Quest/DownloadAppModal'
import { openQuestDialog } from 'src/components/Quest/QuestDialog'
import { SocialMediaFollowModal } from 'src/components/Quest/SocialMediaFollowModal'
import { UIButton } from 'src/components/ui/Button'
import { getLocalizedRecordData } from 'src/i18n/localizedContent'
import {
  useClaimQuestV2Reward,
  useFollowedPlatforms,
  useReportQuestV2UrlVisit,
  useReportSocialFollow,
} from 'src/services/orpc/questV2'
import { localeAtom } from 'src/stores/locale'
import { track } from 'src/utils/tracker'
import { isValidUrl } from 'src/utils/url'
import { questCardOverrideRegistry } from './questCardOverrideRegistry'
import { resolveQuestIcon } from './questIconRegistry'
import { QuestProgressBar } from './QuestProgressBar'
import type { QuestV2 } from './types'
import { useQuestCardViewModel } from './useQuestCardViewModel'
import { useQuestCardDot } from './useQuestDot'

interface QuestCardV2Props {
  quest: QuestV2
  onClaimed?: () => void
}

export function QuestCardV2(props: QuestCardV2Props) {
  // Full-card replacement takes precedence over the default layout — keeps
  // QuestCardV2's render path linear (no conditional hook setup wrapped
  // around half the body). See `questCardOverrideRegistry` for when to
  // reach for this vs. a partial `useQuestCardViewModel` override.
  const OverrideCard = questCardOverrideRegistry[props.quest.buttonAction]
  if (OverrideCard) return <OverrideCard {...props} />
  return <DefaultQuestCardV2 {...props} />
}

function DefaultQuestCardV2({ quest, onClaimed }: QuestCardV2Props) {
  const { t } = useTranslation()
  const locale = useAtomValue(localeAtom)
  // const countdown = useCountdown(quest.endAt)
  const [claiming, setClaiming] = useState(false)
  const [socialModalOpen, setSocialModalOpen] = useState(false)
  const { showDot: baseShowDot, markSeen, viewRef } = useQuestCardDot(quest)
  const { mutateAsync: reportUrlVisit } = useReportQuestV2UrlVisit()
  const { mutateAsync: claimReward } = useClaimQuestV2Reward()
  const { mutateAsync: reportSocialFollow } = useReportSocialFollow()
  const { data: followedPlatforms = [] } = useFollowedPlatforms()

  // Per-buttonAction view model. Default values come from `quest` progress
  // fields; the hook overrides them for "fake quests" whose source of truth
  // lives outside the QuestV2 progress system (e.g. `mainpage_daily_claim`).
  // See `useQuestCardViewModel` for the override registry.
  const questState = useQuestCardViewModel(quest, { baseShowDot })

  const title = getLocalizedRecordData(locale, quest.title)
  const buttonText = getLocalizedRecordData(locale, quest.button)
  const { rewards, isRewarded, derivedStatus, showDot } = questState
  const isUnclaimed = quest.status === 'unclaimed'
  const progress = quest.progress

  const canPersistAfterComplete =
    quest.buttonAction === 'follow_url' &&
    quest.buttonActionConfig.persistAfterComplete

  const handleClick = async () => {
    if ((isRewarded && !canPersistAfterComplete) || claiming) return

    setClaiming(true)
    try {
      await match(quest)
        .with({ buttonAction: 'follow_url' }, async q => {
          const link = q.buttonActionConfig.link
          if (!link) return
          const url = getLocalizedRecordData(locale, link) ?? ''
          if (!isValidUrl(url)) return
          window.open(url, '_blank', 'noopener,noreferrer')
          track('quest_v2_follow_url', { questId: q.id })
          markSeen()
          if (!isRewarded) await reportUrlVisit({ body: { url } })
        })
        .with({ buttonAction: 'claim_reward' }, async q => {
          if (!isUnclaimed) return
          track('quest_v2_claim', { questId: q.id })
          await claimReward({ path: { questId: q.id } })
        })
        .with({ buttonAction: 'participate_survey' }, q => {
          window.open(
            `https://form.typeform.com/to/${q.buttonActionConfig.formId}`,
            '_blank',
            'noopener,noreferrer',
          )
          track('quest_v2_participate_survey', { questId: q.id })
          markSeen()
        })
        .with({ buttonAction: 'open_social_follow_modal' }, q => {
          track('quest_v2_open_social_follow_modal', { questId: q.id })
          setSocialModalOpen(true)
        })
        .with({ buttonAction: 'open_download_app_modal' }, q => {
          track('quest_v2_open_download_app_modal', { questId: q.id })
          markSeen()
          void openDownloadAppModal({
            trackingArgs: { source: 'quest', questId: q.id },
            rewardCount: rewards > 0 ? rewards : undefined,
          })
        })
        .with({ buttonAction: 'mainpage_daily_claim' }, q => {
          track('quest_v2_mainpage_daily_claim', { questId: q.id })
          markSeen()
          openQuestDialog()
        })
        .otherwise(() => {
          // Action types without a frontend dispatch
          // (`show_incentive_ads`, `show_gmo_offerwall`, `daily_claim`,
          // `share_activity`) intentionally fall through here.
        })
      onClaimed?.()
    } finally {
      setClaiming(false)
    }
  }

  const handleSocialFollow = async (platform: string) => {
    await reportSocialFollow({ body: { platform } })
  }

  const isButtonDisabled =
    (isRewarded && !canPersistAfterComplete) ||
    (quest.buttonAction === 'claim_reward' && quest.status === 'ongoing')

  const styles = questCardStyles({ status: derivedStatus })
  // For the persist-after-complete branch the button keeps the `ongoing`
  // styling even when the rest of the card is `rewarded`, so compute its
  // slot styles independently rather than reusing `styles`.
  const buttonSlotStyles =
    canPersistAfterComplete && isRewarded
      ? questCardStyles({ status: 'ongoing' })
      : styles

  return (
    <div
      ref={viewRef}
      className={styles.card({ className: questState.cardClassName })}
    >
      {/* Countdown badge — temporarily disabled
      {countdown && (
        <div className={styles.countdown()}>
          <svg
            className="size-3"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="8" cy="8" r="6.5" />
            <path d="M8 4.5V8l2.5 1.5" />
          </svg>
          {countdown}
        </div>
      )} */}

      {/* Top row: icon + title + claimed indicator */}
      <div className="flex items-center gap-1.5">
        {quest.visibility === 'invisible' && (
          <span className="flex-none rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            Hidden
          </span>
        )}
        {quest.icon &&
          (() => {
            const Icon = resolveQuestIcon(quest.icon)
            return Icon ? (
              <span className="flex size-4 flex-none items-center justify-center text-sm">
                <Icon className="size-4" />
              </span>
            ) : null
          })()}
        <span className={styles.title()}>{title}</span>
        {isRewarded && (
          <svg
            className="size-4 flex-none text-emerald-500"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.28-8.72a.75.75 0 0 0-1.06-1.06L7 8.44 5.78 7.22a.75.75 0 0 0-1.06 1.06l1.75 1.75a.75.75 0 0 0 1.06 0l3.75-3.75Z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {/* Bottom row: rewards/progress + action */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {rewards > 0 && (
            <>
              <GameIconsCrystalBars className={styles.rewardIcon()} />
              <span className={styles.rewardText()}>
                {rewards.toLocaleString()}
              </span>
            </>
          )}
          {progress && (
            <QuestProgressBar
              className="ml-1 flex-1"
              current={progress.current}
              max={progress.total}
            />
          )}
        </div>

        <div className="relative flex-none">
          <UIButton
            variant="unstyled"
            busy={claiming}
            disabled={isButtonDisabled}
            className={buttonSlotStyles.button({
              className: questState.buttonClassName,
            })}
            onPress={handleClick}
          >
            {isRewarded && !canPersistAfterComplete
              ? t('user:quest.claimed')
              : buttonText}
            {showDot && (
              <span className="sr-only">
                {' '}
                {t('user:quest.newIndicator', 'New')}
              </span>
            )}
          </UIButton>
          {showDot && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -top-1 -right-1 size-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-zinc-900"
            />
          )}
        </div>
      </div>

      {quest.buttonAction === 'open_social_follow_modal' && (
        <SocialMediaFollowModal
          isOpen={socialModalOpen}
          onClose={() => setSocialModalOpen(false)}
          completedPlatforms={followedPlatforms}
          onFollowPlatform={handleSocialFollow}
        />
      )}
    </div>
  )
}

const questCardStyles = tv({
  slots: {
    card: 'relative flex min-h-[80px] flex-col gap-2 rounded-lg border p-3',
    countdown:
      'absolute top-2 right-2 flex items-center gap-0.5 text-xs text-zinc-500',
    title: 'truncate text-sm font-semibold',
    rewardIcon: 'size-4 flex-none',
    rewardText: 'flex-none text-sm tabular-nums',
    button:
      'h-9 min-w-[80px] flex-none rounded-md px-4 text-sm font-medium disabled:bg-zinc-300 disabled:text-neutral-600 dark:disabled:bg-zinc-600 dark:disabled:text-neutral-300',
  },
  variants: {
    status: {
      ongoing: {
        card: 'border-zinc-500/50',
        rewardIcon: 'text-theme-primary',
        rewardText: 'text-theme-primary',
        button: 'bg-black text-white dark:bg-white dark:text-black',
      },
      unclaimed: {
        card: 'border-theme-primary/50',
        rewardIcon: 'text-theme-primary',
        rewardText: 'text-theme-primary',
        button: 'bg-black text-white dark:bg-white dark:text-black',
      },
      rewarded: {
        card: 'border-zinc-500/50',
        title: 'opacity-60',
        rewardIcon: 'text-zinc-400 opacity-60 dark:text-zinc-500',
        rewardText: 'text-zinc-400 line-through opacity-60 dark:text-zinc-500',
        button:
          'cursor-default border border-zinc-300 bg-transparent text-zinc-400 opacity-60 dark:border-zinc-600 dark:text-zinc-500',
      },
    },
  },
  defaultVariants: {
    status: 'ongoing',
  },
})
